// src/features/scheduledReports/useScheduledReports.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';

export type ReportType = 'bot_usage' | 'daily_market_summary' | 'top_movers';
export type ScheduleType = 'daily' | 'weekly';

export interface ScheduledReport {
  id: number;
  report_type: ReportType;
  channel_id: string;
  mention_role_id: string | null;
  schedule_type: ScheduleType;
  schedule_time: string | null;
  schedule_day_of_week: number | null;
  schedule_interval_hours: number | null;
  timezone: string;
  enabled: boolean;
  last_sent_at: string | null;
  created_by: string;
  created_at: string;
}

export interface ReportPreview {
  title: string;
  description: string;
  fields: { name: string; value: string }[];
}

export interface ReportInput {
  reportType: ReportType;
  channelId: string;
  mentionRoleId?: string | null;
  scheduleType: ScheduleType;
  scheduleTime?: string | null;
  scheduleDayOfWeek?: number | null;
  scheduleIntervalHours?: number | null;
  timezone?: string;
}

export function useScheduledReports(guildId: string | null) {
  return useQuery({
    queryKey: ['scheduled-reports', guildId],
    queryFn: () => apiFetch<ScheduledReport[]>(`/api/scheduled-reports/${guildId}`),
    enabled: !!guildId,
  });
}

export function useCreateReport(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReportInput) =>
      apiFetch(`/api/scheduled-reports/${guildId}`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scheduled-reports', guildId] }),
  });
}

export function useUpdateReport(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & Partial<ReportInput> & { enabled?: boolean }) =>
      apiFetch(`/api/scheduled-reports/${guildId}/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scheduled-reports', guildId] }),
  });
}

export function useDeleteReport(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/api/scheduled-reports/${guildId}/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scheduled-reports', guildId] }),
  });
}

export function usePreviewReport(guildId: string | null) {
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<ReportPreview>(`/api/scheduled-reports/${guildId}/${id}/preview`, { method: 'POST' }),
  });
}
